import type { TemplateListItemApi, TemplateDetailApi, UpdateTemplateApiRequest, CreateTemplateApiRequest } from "@/types/api/templates";
import type { Template } from "@/types/template";

function contentToHtml(text: string): string {
  if (!text) return "";
  if (text.trimStart().startsWith("<")) return text;
  const normalized = text.replace(/\\n/g, "\n");
  return normalized
    .split(/\n\n+/)
    .map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`)
    .join("");
}

export function decodeHtmlEntities(text: string): string {
  if (!text) return "";
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
}

function stripHtmlTags(html: string): string {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").trim();
}

function convertVariableSpansToText(html: string): string {
  if (!html) return "";
  // Convert variable span nodes back to raw <<key>> placeholders
  let result = html.replace(/<span[^>]*data-variable="([^"]+)"[^>]*>[^<]*<\/span>/g, "$1");
  // Decode HTML-encoded angle brackets that TipTap produces when users type <<key>> as plain text
  result = result.replace(/&lt;&lt;([^&]+)&gt;&gt;/g, "<<$1>>");
  return result;
}

const templateTypeIdMap: Record<Template["type"], number> = {
  email: 1,
  statement: 2,
};

export function mapTemplateListItemApiToUi(api: TemplateListItemApi): Template {
  const typeName = api.templateTypeName.toLowerCase();
  const type: Template["type"] = typeName === "statement" ? "statement" : "email";

  return {
    id: String(api.id),
    name: api.name,
    type,
    subject: decodeHtmlEntities(api.subject ?? ""),
    content: "",
    isDefault: api.isDefault,
    category: api.category
      ? [api.category]
      : api.templateCategoryTypeName
        ? [api.templateCategoryTypeName]
        : [],
    categoryTypeId: api.templateCategoryTypeId ?? undefined,
    categoryTypeName: api.templateCategoryTypeName ?? undefined,
    variables: [],
    lastModified: api.modifiedAt ?? api.createdAt,
    createdAt: api.createdAt,
    createdBy: api.createdByUserName ?? "",
  };
}

function normalizeFieldsSettings(pdfSettings: Template["pdfSettings"]): void {
  if (!pdfSettings?.fieldsSettings) return;
  const { accountSummary, totals } = pdfSettings.fieldsSettings;

  // Migrate legacy currentBalance → totalOverdue + totalNotYetDue in account summary
  if (accountSummary?.items) {
    const items = accountSummary.items as Record<string, boolean>;
    if ('currentBalance' in items && !('totalNotYetDue' in items)) {
      items.totalOverdue = items.totalOverdue ?? items.currentBalance;
      items.totalNotYetDue = items.currentBalance;
      delete items.currentBalance;
    }
  }

  // Ensure totals has totalNotYetDue (added alongside existing totalOverdue)
  if (totals?.items) {
    const items = totals.items as Record<string, boolean>;
    if (!('totalNotYetDue' in items)) {
      items.totalNotYetDue = items.totalOverdue ?? false;
    }
  }
}

export function mapTemplateDetailApiToUi(api: TemplateDetailApi): Template {
  const typeName = api.templateTypeName.toLowerCase();
  const type: Template["type"] = typeName === "statement" ? "statement" : "email";

  let pdfSettings: Template["pdfSettings"] | undefined;
  if (api.pdfSettings) {
    try {
      pdfSettings = JSON.parse(api.pdfSettings) as Template["pdfSettings"];
      normalizeFieldsSettings(pdfSettings);
    } catch {
      // ignore malformed JSON
    }
  }

  return {
    id: String(api.id),
    name: api.name,
    type,
    subject: stripHtmlTags(decodeHtmlEntities(api.subject ?? "")),
    content: contentToHtml(api.content),
    isDefault: api.isDefault,
    category: api.category
      ? [api.category]
      : api.templateCategoryTypeName
        ? [api.templateCategoryTypeName]
        : [],
    categoryTypeId: api.templateCategoryTypeId ?? undefined,
    categoryTypeName: api.templateCategoryTypeName ?? undefined,
    variables: api.variables ? api.variables.split(",").map((v) => v.trim()) : [],
    lastModified: api.modifiedAt ?? api.createdAt,
    createdAt: api.createdAt,
    createdBy: api.createdByUserName ?? "",
    thumbnail: api.thumbnail ?? undefined,
    pdfSettings,
    attachInvoiceCopies: api.attachInvoiceCopies ?? false,
  };
}

export function mapTemplateUiToUpdateRequest(template: Template): UpdateTemplateApiRequest {
  return {
    name: template.name,
    templateTypeId: templateTypeIdMap[template.type],
    outputTypeId: template.type === "statement" ? 2 : 1,
    subject: stripHtmlTags(template.subject ?? ""),
    content: convertVariableSpansToText(template.content),
    isDefault: template.isDefault,
    category: template.category?.join(", "),
    templateCategoryTypeId: template.categoryTypeId ?? undefined,
    variables: template.variables?.join(", "),
    pdfSettings: template.pdfSettings ? JSON.stringify(template.pdfSettings) : undefined,
    thumbnail: template.thumbnail,
    attachInvoiceCopies: template.attachInvoiceCopies,
  };
}

export function mapTemplateUiToCreateRequest(template: Template): CreateTemplateApiRequest {
  return {
    name: template.name,
    templateTypeId: templateTypeIdMap[template.type],
    outputTypeId: template.type === "statement" ? 2 : 1,
    subject: stripHtmlTags(template.subject ?? ""),
    content: convertVariableSpansToText(template.content),
    isDefault: template.isDefault,
    category: template.category?.join(", "),
    templateCategoryTypeId: template.categoryTypeId ?? undefined,
    variables: template.variables?.join(", "),
    pdfSettings: template.pdfSettings ? JSON.stringify(template.pdfSettings) : undefined,
    thumbnail: template.thumbnail,
    attachInvoiceCopies: template.attachInvoiceCopies,
  };
}
